import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Mail, Settings, User as UserIcon } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { fetchInbox, UNREAD_EVENT } from '../services/boiteLettresService';

export default function NavTop({ language = 'fr' }) {
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const refreshUnread = useCallback(async () => {
    if (!user?.id) {
      setUnreadCount(0);
      return;
    }
    try {
      const { messages } = await fetchInbox();
      const unread = (messages || []).filter((m) => !m.is_read).length;
      setUnreadCount(unread);
    } catch (_err) {
      setUnreadCount(0);
    }
  }, [user?.id]);

  useEffect(() => {
    refreshUnread();
  }, [refreshUnread]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = () => refreshUnread();
    window.addEventListener(UNREAD_EVENT, handler);
    return () => window.removeEventListener(UNREAD_EVENT, handler);
  }, [refreshUnread]);

  const badgeLabel = unreadCount > 9 ? '9+' : String(unreadCount);

  const tapAnim = reducedMotion ? undefined : { scale: 0.92 };

  return (
    <nav
      className="sticky top-0 left-0 right-0 z-[10000]"
      style={{
        background: 'rgba(255, 247, 245, 0.92)',
        backdropFilter: 'blur(20px) saturate(140%)',
        WebkitBackdropFilter: 'blur(20px) saturate(140%)',
        borderBottom: '1px solid var(--cb-border)',
        boxShadow: '0 4px 18px rgba(122,101,88,0.06)',
        minHeight: 56,
      }}
      data-testid="nav-top"
      aria-label={language === 'fr' ? 'Navigation principale' : 'Main navigation'}
    >
      <div className="max-w-lg mx-auto flex items-center justify-between h-14 px-4 nav-top-row">
        <motion.button
          type="button"
          onClick={() => navigate('/')}
          whileTap={tapAnim}
          className="flex items-center justify-center"
          style={{
            fontFamily: 'Caveat, cursive',
            fontSize: 26,
            color: 'var(--cb-primary)',
            fontWeight: 600,
            lineHeight: 1,
            minHeight: 44,
          }}
          aria-label="ColoBongo - accueil"
          data-testid="navtop-wordmark"
        >
          ColoBongo
        </motion.button>

        <div className="flex items-center gap-3">
          <motion.button
            type="button"
            onClick={() => navigate('/boite-lettres')}
            whileTap={tapAnim}
            className="relative flex items-center justify-center rounded-full"
            style={{
              width: 44,
              height: 44,
              color: 'var(--cb-text)',
            }}
            aria-label={
              language === 'fr'
                ? `Boîte à lettres${unreadCount > 0 ? `, ${unreadCount} non lus` : ''}`
                : `Inbox${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`
            }
            data-testid="navtop-inbox"
          >
            <Mail size={22} strokeWidth={1.9} />
            {unreadCount > 0 && (
              <span
                aria-hidden="true"
                className="absolute"
                style={{
                  top: 6,
                  right: 6,
                  minWidth: 16,
                  height: 16,
                  borderRadius: 9999,
                  padding: '0 4px',
                  background: 'var(--cb-primary)',
                  color: 'var(--cb-text-on-primary, #fff)',
                  fontSize: 10,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 0 0 2px rgba(255,247,245,0.95)',
                }}
              >
                {badgeLabel}
              </span>
            )}
          </motion.button>

          <motion.button
            type="button"
            onClick={() => navigate('/profile')}
            whileTap={tapAnim}
            className="flex items-center justify-center rounded-full"
            style={{
              width: 44,
              height: 44,
            }}
            aria-label={language === 'fr' ? 'Mon profil' : 'My profile'}
            data-testid="navtop-avatar"
          >
            <span
              className="flex items-center justify-center rounded-full"
              style={{
                width: 32,
                height: 32,
                background: 'var(--cb-peach-100, rgba(247,212,184,0.55))',
                color: 'var(--cb-primary-dark, var(--cb-primary))',
                boxShadow: 'inset 0 0 0 1px rgba(232, 132, 92, 0.20)',
              }}
            >
              <UserIcon size={18} strokeWidth={2} />
            </span>
          </motion.button>

          <motion.button
            type="button"
            onClick={() => navigate('/settings')}
            whileTap={tapAnim}
            className="flex items-center justify-center rounded-full"
            style={{
              width: 44,
              height: 44,
              color: 'var(--cb-text-muted)',
            }}
            aria-label={language === 'fr' ? 'Réglages' : 'Settings'}
            data-testid="navtop-settings"
          >
            <Settings size={20} strokeWidth={1.9} />
          </motion.button>
        </div>
      </div>
      <style>{`
        @media (max-width: 360px) {
          [data-testid="nav-top"] .nav-top-row {
            padding-left: 12px;
            padding-right: 12px;
            gap: 4px;
          }
          [data-testid="navtop-wordmark"] {
            font-size: 22px !important;
          }
        }
      `}</style>
    </nav>
  );
}
